// Logo ring colors — "Empowering Farmer" emblem
const GREEN_MAIN  = "#3D8A42"; // green ring
const GREEN_DARK  = "#2A6B2E"; // tree figure / dark green
const GREEN_LIGHT = "#EAF4EB"; // light green tint
const YELLOW_MAIN = "#F5BE1A"; // yellow/gold ring
const YELLOW_LIGHT= "#FFF9E5"; // light yellow tint
const BLUE_MAIN   = "#29ABE2"; // sky-blue ring
const BLUE_LIGHT  = "#E6F6FD"; // light blue tint
const BROWN_MAIN  = "#9B6B3A"; // earth-brown ring

const WHITE       = "#ffffff";
const TEXT_DARK   = "#1a1a1a";
const TEXT_MID    = "#555555";
const BORDER_MID  = "#e4e4e4";

const S = {
  // ── Floating Action Button ─────────────────────────────────────────────
  fab: {
    position: "fixed",
    bottom: "1.5rem",
    right: "1.5rem",
    zIndex: 1010,
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${GREEN_MAIN} 0%, ${GREEN_DARK} 100%)`,
    color: WHITE,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.3rem",
    boxShadow: `0 4px 20px rgba(61,138,66,0.55)`,
    transition: "transform 0.18s, box-shadow 0.18s",
    outline: "none",
  },
  fabLabel: {
    position: "fixed",
    bottom: "1.75rem",
    right: "5rem",
    zIndex: 1010,
    background: `linear-gradient(90deg, ${GREEN_DARK} 0%, ${GREEN_MAIN} 100%)`,
    color: WHITE,
    padding: "0.3rem 0.85rem",
    borderRadius: 20,
    fontSize: "0.78rem",
    fontWeight: 700,
    pointerEvents: "none",
    boxShadow: "0 2px 10px rgba(61,138,66,0.4)",
    whiteSpace: "nowrap",
    letterSpacing: "0.02em",
  },

  // ── Chat panel ──────────────────────────────────────────────────────────
  panel: {
    position: "fixed",
    bottom: "5rem",
    right: "1.5rem",
    zIndex: 1010,
    width: "min(660px, calc(100vw - 3rem))",
    height: "min(850px, calc(100vh - 6rem))",
    borderRadius: 16,
    backgroundColor: "#f8faf8",
    boxShadow: "0 8px 40px rgba(0,0,0,0.16)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    border: `1px solid ${GREEN_MAIN}30`,
  },

  // ── Header ─────────────────────────────────────────────────────────────
  header: {
    background: `linear-gradient(135deg, ${GREEN_DARK} 0%, ${GREEN_MAIN} 100%)`,
    color: WHITE,
    padding: "0.75rem 1rem",
    display: "flex",
    alignItems: "center",
    gap: "0.65rem",
    flexShrink: 0,
  },
  headerAvatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    objectFit: "cover",
    border: "2px solid rgba(255,255,255,0.35)",
    flexShrink: 0,
    backgroundColor: GREEN_DARK,
  },
  headerInfo: { flex: 1, minWidth: 0 },
  headerTitle: {
    fontWeight: 700,
    fontSize: "0.95rem",
    letterSpacing: "0.01em",
    lineHeight: 1.2,
  },
  headerSubtitle: {
    fontSize: "0.68rem",
    color: "rgba(255,255,255,0.72)",
    lineHeight: 1,
    marginTop: 2,
  },
  // Yellow "online" dot — references gold ring in logo
  headerDot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    backgroundColor: YELLOW_MAIN,
    flexShrink: 0,
    boxShadow: `0 0 0 2px rgba(245,190,26,0.4)`,
  },
  closeBtn: {
    background: "rgba(255,255,255,0.15)",
    border: "none",
    color: "rgba(255,255,255,0.9)",
    cursor: "pointer",
    fontSize: "0.9rem",
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    flexShrink: 0,
    outline: "none",
    transition: "background 0.15s",
  },

  // ── Message area ────────────────────────────────────────────────────────
  messageArea: {
    flex: 1,
    overflowY: "auto",
    padding: "0.85rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.55rem",
    backgroundColor: "#f5f8f5",
  },

  // ── User bubble — green ring ────────────────────────────────────────────
  userBubble: {
    // No maxWidth here — the wrapping div in MessageBubble already caps this
    // at 80%. Setting it again here compounded into ~62% of the message area,
    // which wrapped even short queries onto multiple lines unnecessarily.
    alignSelf: "flex-end",
    background: `linear-gradient(135deg, ${GREEN_MAIN} 0%, ${GREEN_DARK} 100%)`,
    color: WHITE,
    padding: "0.6rem 0.9rem",
    borderRadius: "18px 18px 4px 18px",
    fontSize: "0.87rem",
    lineHeight: 1.45,
    wordBreak: "break-word",
    boxShadow: `0 2px 8px rgba(61,138,66,0.25)`,
  },

  // ── Bot wrapper & bubble — white with blue-ring accent ──────────────────
  botWrapper: {
    alignSelf: "flex-start",
    maxWidth: "90%",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  botBubble: {
    backgroundColor: WHITE,
    color: TEXT_DARK,
    padding: "0.6rem 0.9rem",
    borderRadius: "4px 18px 18px 18px",
    fontSize: "0.87rem",
    lineHeight: 1.5,
    boxShadow: "0 1px 5px rgba(0,0,0,0.07)",
    wordBreak: "break-word",
    borderLeft: `3px solid ${BLUE_MAIN}`,
  },
  uploadedImageThumb: {
    maxWidth: 160,
    maxHeight: 120,
    borderRadius: 8,
    marginBottom: 4,
    display: "block",
  },
  msgTime: {
    fontSize: "0.6rem",
    color: "#aaaaaa",
    marginTop: 2,
    paddingLeft: 4,
  },

  // ── Result cards ────────────────────────────────────────────────────────
  // Product card — blue-ring tint
  resultCard: {
    backgroundColor: BLUE_LIGHT,
    border: `1px solid ${BLUE_MAIN}35`,
    borderLeft: `3px solid ${BLUE_MAIN}`,
    borderRadius: 10,
    padding: "0.7rem 0.9rem",
    fontSize: "0.82rem",
    marginTop: 2,
  },
  // Crop-issue card overrides — green-ring tint (applied inline on crop_problem cards)
  resultCardCrop: {
    backgroundColor: GREEN_LIGHT,
    border: `1px solid ${GREEN_MAIN}35`,
    borderLeft: `3px solid ${GREEN_MAIN}`,
    borderRadius: 10,
    padding: "0.7rem 0.9rem",
    fontSize: "0.82rem",
    marginTop: 2,
  },
  cardBadgeProduct: {
    display: "inline-block",
    background: `linear-gradient(90deg, #1A8EB8 0%, ${BLUE_MAIN} 100%)`,
    color: WHITE,
    borderRadius: 10,
    padding: "2px 10px",
    fontSize: "0.67rem",
    fontWeight: 700,
    marginBottom: 6,
    letterSpacing: "0.04em",
  },
  cardBadgeCrop: {
    display: "inline-block",
    background: `linear-gradient(90deg, ${GREEN_DARK} 0%, ${GREEN_MAIN} 100%)`,
    color: WHITE,
    borderRadius: 10,
    padding: "2px 10px",
    fontSize: "0.67rem",
    fontWeight: 700,
    marginBottom: 6,
    letterSpacing: "0.04em",
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: "0.9rem",
    color: TEXT_DARK,
    marginBottom: 2,
    lineHeight: 1.3,
  },
  cardSubtitle: {
    fontSize: "0.75rem",
    color: TEXT_MID,
    marginBottom: 5,
  },
  cardRow: {
    fontSize: "0.79rem",
    color: "#333333",
    marginBottom: 3,
    lineHeight: 1.45,
  },

  // ── Typing dots — cycle through logo ring colors ────────────────────────
  typingIndicator: {
    alignSelf: "flex-start",
    backgroundColor: WHITE,
    padding: "0.6rem 0.9rem",
    borderRadius: "4px 18px 18px 18px",
    boxShadow: "0 1px 5px rgba(0,0,0,0.07)",
    display: "flex",
    alignItems: "center",
    gap: 6,
    borderLeft: `3px solid ${BLUE_MAIN}`,
  },
  // Individual dot colors set inline (green, yellow, blue)
  typingDotGreen:  { width: 8, height: 8, borderRadius: "50%", backgroundColor: GREEN_MAIN, display: "inline-block" },
  typingDotYellow: { width: 8, height: 8, borderRadius: "50%", backgroundColor: YELLOW_MAIN, display: "inline-block" },
  typingDotBlue:   { width: 8, height: 8, borderRadius: "50%", backgroundColor: BLUE_MAIN,  display: "inline-block" },

  // ── Image preview strip — brown-ring tint ──────────────────────────────
  previewStrip: {
    backgroundColor: YELLOW_LIGHT,
    borderTop: `1px solid ${YELLOW_MAIN}50`,
    padding: "0.35rem 0.75rem",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: "0.75rem",
    color: BROWN_MAIN,
    flexShrink: 0,
  },
  previewStripThumb: {
    width: 28,
    height: 28,
    borderRadius: 5,
    objectFit: "cover",
    flexShrink: 0,
    border: `1px solid ${YELLOW_MAIN}60`,
  },
  previewStripName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.74rem",
    color: BROWN_MAIN,
  },
  previewRemove: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#999",
    fontSize: "0.75rem",
    padding: "2px 4px",
    flexShrink: 0,
    outline: "none",
  },

  // ── Input area ─────────────────────────────────────────────────────────
  inputRow: {
    borderTop: `1px solid ${BORDER_MID}`,
    backgroundColor: WHITE,
    padding: "0.6rem 0.75rem",
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    flexShrink: 0,
  },
  textInput: {
    flex: 1,
    border: `1.5px solid ${BORDER_MID}`,
    borderRadius: 22,
    padding: "0.45rem 0.9rem",
    fontSize: "0.87rem",
    outline: "none",
    backgroundColor: "#f9faf9",
    color: TEXT_DARK,
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  iconBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#aaaaaa",
    fontSize: "1.05rem",
    padding: "4px 6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    outline: "none",
    transition: "color 0.15s",
  },
  sendBtn: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${GREEN_MAIN} 0%, ${GREEN_DARK} 100%)`,
    color: WHITE,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.85rem",
    flexShrink: 0,
    outline: "none",
    boxShadow: `0 2px 8px rgba(61,138,66,0.4)`,
    transition: "opacity 0.15s",
  },
  dataLoadingMsg: {
    padding: "0.65rem 1rem",
    textAlign: "center",
    fontSize: "0.78rem",
    color: GREEN_DARK,
    backgroundColor: GREEN_LIGHT,
    borderBottom: `1px solid ${GREEN_MAIN}30`,
    flexShrink: 0,
  },
};

export default S;
